import React from 'react'
import { tokens } from "../../theme";
import { Box, Typography, useTheme, CircularProgress, LinearProgress, Divider, TextField, FormControl, Button, ToggleButton, ToggleButtonGroup, Chip, Dialog, DialogTitle, DialogContent, IconButton } from "@mui/material";
import Header from "../../components/Header";
import Axios from 'axios';
import Loading from '../../components/Loading';
import Alert from '../../components/alert/Alert';
import { useSignOut } from "react-auth-kit";
import { useNavigate } from "react-router-dom";
import { Document, Page, pdfjs } from "react-pdf";
import './Viewer.css';
import 'mathlive';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import getCookies from '../../utils/getCookies';
import FileUpload from '../../components/FileUpload';
import { EditorTiny } from '../../components/EditorTiny';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ExerciseEditModal from './ExerciseEditModal';
import { getAuthHeader } from '../../utils/authToken';
import QuestionRender from '../../components/QuestionRender';
import { CoordinateGrid, defaultCoordinateConfig, formatElementSummary, AnswerLogicSummary, hydrateElements } from '../../components/coordinateGrid';
import { TableReadOnly, defaultTableConfig } from '../../components/tableAnswer';
import { MappingReadOnly, defaultMappingConfig } from '../../components/mappingAnswer';
import { NumberLineReadOnly, NumberLine as NumberLineSVG, defaultNumberLineConfig, formatElementSummary as formatNLElementSummary } from '../../components/numberLine';
import { PolynomialGraphReadOnly, PolynomialGraphStudent, defaultPolynomialGraphConfig } from '../../components/polynomialGraph';
import { RationalGraphReadOnly, RationalGraphStudent, defaultRationalGraphConfig } from '../../components/rationalGraph';
import { TrigGraphStudent, TrigGraphReadOnly, defaultTrigGraphConfig } from '../../components/trigGraph';
import { MultilineReadOnly, defaultMultilineConfig } from '../../components/multiline';
import { MATHLIVE_ANSWER_TYPES, parseMathliveAnswer } from '../../components/mathlive';
import HwQuestionForm from '../hwQuestions/HwQuestionForm';
import HwQuestionCropTool from '../hwQuestions/HwQuestionCropTool';
import HwQuestionPageRangeSelector from '../hwQuestions/HwQuestionPageRangeSelector';
import ExtractionJobMonitor from '../hwQuestions/ExtractionJobMonitor';
import ExtractedQuestionsReview from '../hwQuestions/ExtractedQuestionsReview';
import RecentExtractionJobs from '../hwQuestions/RecentExtractionJobs';
import HistoryIcon from '@mui/icons-material/History';
import CloseIcon from '@mui/icons-material/Close';
import ReorderQuestionsModal from './ReorderQuestionsModal';
import CopyQuestionsModal from './CopyQuestionsModal';
import ConfirmDialog from '../../components/ConfirmDialog';

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;

const GraphStudentPreview = ({ Component, config, defaultConfig }) => {
    const [answer, setAnswer] = React.useState({});
    return (
        <Component
            config={config || defaultConfig}
            value={answer}
            onChange={setAnswer}
            showCorrectness
        />
    );
};

const TextbookExercises = () => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const currentUser = getCookies("_auth_state");
    const userRole = currentUser ? currentUser.split(',')[2]?.split(':')[1]?.split('"')[1] : null;
    const textbookId = window.location.pathname.split("/")[window.location.pathname.split("/").length - 1];
    
    // for 401 error
    const signOut = useSignOut();
    const navigate = useNavigate();
    const handleLogout = () => {
        signOut();
        localStorage.removeItem("userName");
        localStorage.removeItem("who");
        navigate("/signin");
    }

    const [loading, setLoading] = React.useState(true);
    const [updated, setUpdated] = React.useState(false);
    const [pdfLoading, setPdfLoading] = React.useState(true);
    const [textbook, setTextbook] = React.useState(null);

    const [selectedPage, setSelectedPage] = React.useState(1);
    const [numPages, setNumPages] = React.useState(null);

    // Ref for PDF container (used by crop tool)
    const pdfContainerRef = React.useRef(null);

    // for alert
    const [successAlertOpen, setSuccessAlertOpen] = React.useState(false);
    const [errorAlertOpen, setErrorAlertOpen] = React.useState(false);
    const [successAlertMessage, setSuccessAlertMessage] = React.useState("");
    const [errorAlertMessage, setErrorAlertMessage] = React.useState("");

    // hwQuestion mode states
    const [hwQuestionMode, setHwQuestionMode] = React.useState(false); // false = View, true = Add
    const [addSubMode, setAddSubMode] = React.useState("manual"); // "manual", "crop", "page_range"

    // AI-assisted extraction states
    const [extractionJobId, setExtractionJobId] = React.useState(null);
    const [extractedQuestions, setExtractedQuestions] = React.useState([]);
    const [showExtractionMonitor, setShowExtractionMonitor] = React.useState(false);
    const [showQuestionsReview, setShowQuestionsReview] = React.useState(false);
    const [showRecentJobsModal, setShowRecentJobsModal] = React.useState(false);

    const confirmRef = React.useRef();

    React.useEffect(() => {
        localStorage.setItem('selectedMenu', 'Textbooks');

        async function fetchTextbook () {
            await Axios.get(`${process.env.REACT_APP_URL}/api/v1/textbooks/${textbookId}`, {
                headers: {
                    Authorization: getAuthHeader(),
                },
            }).then(async (response) => {
                setTextbook(response.data);
            }).catch((error) => {
                if (error.response?.status === 404) {
                    setLoading(false);
                    setErrorAlertMessage('Textbook not found!');
                    setErrorAlertOpen(true);
                    return;
                } else if (error.response?.status === 401) {
                    handleLogout();
                    return;
                }
                setErrorAlertMessage(error.response?.data?.message || "Something went wrong");
                setErrorAlertOpen(true);
            });
        }

        fetchTextbook();
        setLoading(false);
        setUpdated(false);
    }, [updated]);

    // PDF load progress state
    const [pdfLoadProgress, setPdfLoadProgress] = React.useState(0);

    function onDocumentLoadSuccess({ numPages }) {
        setNumPages(numPages);
    }

    // Memoize pdf.js options to prevent re-triggering Document load on every render.
    // disableAutoFetch stops pdf.js from pre-downloading all pages after the first loads.
    const pdfOptions = React.useMemo(() => ({
        disableAutoFetch: true,
        disableStream: false,
    }), []);

    const documentRender = () => {
        // Use devicePixelRatio to render crisp PDFs on high-DPI displays (Windows scaling, Retina, etc.)
        // Minimum scale of 2 ensures crisp rendering even at DPR 1
        const pixelRatio = Math.max(window.devicePixelRatio || 1, 2);

        return (
            <Document
                file={textbook?.file_url}
                options={pdfOptions}
                onLoadSuccess={onDocumentLoadSuccess}
                onLoadProgress={({ loaded, total }) => {
                    if (total > 0) setPdfLoadProgress(Math.round((loaded / total) * 100));
                }}
                onContextMenu={(e) => e.preventDefault()}
                className="pdf-container"
                loading={
                    <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight="400px" gap={4}>
                        <CircularProgress color="inherit" size={40} />
                        {pdfLoadProgress > 0 && pdfLoadProgress < 100 && (
                            <Box width="60%">
                                <LinearProgress variant="determinate" value={pdfLoadProgress} />
                                <Typography variant="caption" textAlign="center" display="block" mt={0.5}>
                                    Loading PDF... {pdfLoadProgress}%
                                </Typography>
                            </Box>
                        )}
                    </Box>
                }
            >
                <div>
                    <Page
                        key={`page_${selectedPage}`}
                        pageNumber={selectedPage}
                        renderTextLayer={false}
                        renderAnnotationLayer={false}
                        scale={pixelRatio}
                        onRenderSuccess={() => setPdfLoading(false)}
                        loading={
                            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                                <CircularProgress color="inherit" size={40} />
                            </Box>
                        }
                    />
                </div>
            </Document>
        );
    };

    const getSubjectString = (subject_1, subject_2, subject_3, subject_4) => {
        if (subject_3 === null && subject_4 === null) {
            return `${subject_1} - ${subject_2}`;
        } else if (subject_3 !== null && subject_4 === null) {
            return `${subject_1} - ${subject_2} - ${subject_3}`;
        } else if (subject_3 !== null && subject_4 !== null) {
            return `${subject_1} - ${subject_2} - ${subject_3} - ${subject_4}`;
        } else {
            return `${subject_1}`;
        }
    };

    const flexDirectionForAnswer = (answer_tool, question_type) => {
        if (answer_tool === 'tinymce' || question_type === 'file_upload') {
            return 'column';
        } else {
            return 'row';
        }
    };

    const alignItemsForAnswer = (answer_tool, question_type) => {
        if (answer_tool === 'tinymce' || question_type === 'file_upload') {
            return 'flex-start';
        } else {
            return 'center';
        }
    };

    const handlePageChange = (e, upOrDown) => {
        e.preventDefault();
        if (upOrDown === 1) {
            if (selectedPage === numPages) {
                return;
            }
            setPdfLoading(true);
            setSelectedPage(selectedPage + 1);
        } else {
            if (selectedPage === 1) {
                return;
            }
            setPdfLoading(true);
            setSelectedPage(selectedPage - 1);
        }
    };

    const handleDelete = (e, questionId, questionLabel) => {
        e.preventDefault();
        confirmRef.current?.open({
            title: 'Delete Question',
            message: `Are you sure you want to delete "${questionLabel}"? This action cannot be undone.`,
            confirmLabel: 'Delete',
            onConfirm: () => deleteQuestion(questionId),
        });
    };

    const deleteQuestion = (questionId) => {
        Axios.delete(`${process.env.REACT_APP_URL}/api/v1/hw-questions/${questionId}`, {
            headers: {
                Authorization: getAuthHeader(),
            },
        }).then(() => {
            setSuccessAlertMessage('Question deleted successfully!');
            setSuccessAlertOpen(true);
            setUpdated(true);
        }).catch((error) => {
            setErrorAlertMessage(error.response?.data?.message || "Something went wrong");
            setErrorAlertOpen(true);
        });
    };

    // Mode toggle handlers
    const handleModeToggle = (_, newMode) => {
        if (newMode === null) return; // Prevent deselection

        const isAddMode = newMode === "add";
        setHwQuestionMode(isAddMode);

        // Reset extraction states when switching modes
        if (!isAddMode) {
            setShowExtractionMonitor(false);
            setShowQuestionsReview(false);
            setExtractedQuestions([]);
            setExtractionJobId(null);
        }
    };

    const handleSubModeChange = (_, newSubMode) => {
        if (newSubMode === null) return;
        setAddSubMode(newSubMode);

        // Reset extraction states when switching sub-modes
        setShowExtractionMonitor(false);
        setShowQuestionsReview(false);
        setExtractedQuestions([]);
        setExtractionJobId(null);
    };

    // Manual question submit handler
    const handleManualQuestionSubmit = () => {
        setSuccessAlertMessage('Question added successfully!');
        setSuccessAlertOpen(true);
        setUpdated(true); // Refresh questions list
        setHwQuestionMode(false); // Switch back to view mode
    };

    // Error handler for extraction tools
    const handleExtractionError = (message) => {
        setErrorAlertMessage(message);
        setErrorAlertOpen(true);
    };

    return (
        <Box m="20px" marginX={3} marginY={3} >
            <Header title={textbook?.title} subtitle="A ONE Institute" />
            { loading === null ?
                <Loading />
            : <>
                {/* Mode Toggle UI */}
                <Box mb={3} mt={-1}>
                    <Typography variant="h6" fontWeight="bold" mb={2}>
                        Mode
                    </Typography>
                    <ToggleButtonGroup
                        color="primary"
                        value={hwQuestionMode ? "add" : "view"}
                        exclusive
                        onChange={handleModeToggle}
                        sx={{
                            '& .MuiToggleButton-root': {
                                padding: '10px 20px',
                                fontWeight: 'bold',
                                '&.Mui-selected': {
                                    backgroundColor: colors.blueAccent[700],
                                    color: '#fff',
                                    '&:hover': {
                                        backgroundColor: colors.blueAccent[800],
                                    },
                                },
                            },
                        }}
                    >
                        <ToggleButton value="view">View Questions</ToggleButton>
                        <ToggleButton value="add">Add Questions</ToggleButton>
                    </ToggleButtonGroup>
                    <Button
                        variant="outlined"
                        startIcon={<HistoryIcon />}
                        onClick={() => setShowRecentJobsModal(true)}
                        sx={{ ml: 2 }}
                    >
                        Recent Extraction Jobs
                    </Button>
                    <CopyQuestionsModal
                        targetTextbookId={textbookId}
                        targetTextbookTitle={textbook?.title}
                        setSuccessAlertOpen={setSuccessAlertOpen}
                        setErrorAlertOpen={setErrorAlertOpen}
                        setSuccessAlertMessage={setSuccessAlertMessage}
                        setErrorAlertMessage={setErrorAlertMessage}
                        setUpdated={setUpdated}
                    />

                    {hwQuestionMode && (
                        <Box mt={2}>
                            <Typography variant="h6" mb={1}>
                                Add Method
                            </Typography>
                            <ToggleButtonGroup
                                color="secondary"
                                value={addSubMode}
                                exclusive
                                onChange={handleSubModeChange}
                                sx={{
                                    '& .MuiToggleButton-root': {
                                        padding: '10px 20px',
                                        fontWeight: 'bold',
                                        '&.Mui-selected': {
                                            backgroundColor: colors.greenAccent[600],
                                            color: '#000',
                                            '&:hover': {
                                                backgroundColor: colors.greenAccent[700],
                                            },
                                        },
                                    },
                                }}
                            >
                                <ToggleButton value="manual">Manual Entry</ToggleButton>
                                <ToggleButton value="crop">Crop Mode (OCR)</ToggleButton>
                                <ToggleButton value="page_range">Page Range (OCR)</ToggleButton>
                            </ToggleButtonGroup>
                        </Box>
                    )}
                </Box>

                <Box display="flex" flexDirection="row" justifyContent="left" gap={2} p={0} mb={1} mt={-2}>
                    <Typography variant="h4">
                        <span style={{ fontWeight: 'bold' }}>Category:</span> {getSubjectString(textbook?.subject_1, textbook?.subject_2, textbook?.subject_3, textbook?.subject_4)}
                    </Typography>
                    <Typography variant="h4">
                        <span style={{ fontWeight: 'bold' }}>Total Pages:</span> {textbook?.pages}
                    </Typography>
                </Box>
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        gap: '20px',
                        marginBottom: '10px',
                        position: 'relative',
                    }}
                >
                    {/* Open PDF */}
                    <a href={textbook?.file_url} target="_blank" rel="noreferrer" style={{'textDecoration': 'none'}}>
                        <Typography 
                            sx={{
                                'fontWeight': 'bold', 
                                'cursor': 'pointer',
                                'position': 'absolute',
                                'top': '50%',
                                'transform': 'translateY(-50%)',
                                'left': '0',
                                display: 'flex',
                                gap: '10px',
                                alignItems: 'center',
                                padding: '10px',
                                borderRadius: '5px',
                                backgroundColor: colors.greenAccent[500],
                                boxShadow: '0 0 10px 0 rgba(0, 0, 0, 0.2)',
                                color: colors.primary[400],
                            }}
                        >
                            Open Textbook
                            <OpenInNewIcon fontSize='small' sx={{ cursor: 'pointer' }} />
                        </Typography>
                    </a>
                    {/* Page Navigator */}
                    <NavigateBeforeIcon fontSize='large' sx={{ cursor: selectedPage === 1 ? 'not-allowed' : 'pointer' }} onClick={(e) => handlePageChange(e, 0)} />
                    <TextField
                        variant="outlined"
                        type="number"
                        label="Page"
                        value={selectedPage}
                        onChange={(e) => {
                            const val = parseInt(e.target.value);
                            if (!isNaN(val) && val >= 1 && val <= numPages) {
                                setPdfLoading(true);
                                setSelectedPage(val);
                            }
                        }}
                        sx={{ 
                            maxWidth: '60px',
                            '& input': {
                                textAlign: 'center',
                            },
                            '& input::-webkit-inner-spin-button': {
                                WebkitAppearance: 'none',
                                margin: 0,
                            },
                            '& input::-webkit-outer-spin-button': {
                                WebkitAppearance: 'none',
                                margin: 0,
                            },
                        }}
                    />
                    <NavigateNextIcon fontSize='large' sx={{ cursor: selectedPage === numPages ? 'not-allowed' : 'pointer' }} onClick={(e) => handlePageChange(e, 1)} />
                </Box>
                <Box
                    display="flex"
                    width="100%"
                    bgcolor={colors.primary[600]}
                >
                    <Box flex={8} width="100%" position="relative">
                        <Box
                            ref={pdfContainerRef}
                            sx={{
                                position: "sticky",
                                top: 0,
                                '& > *': {
                                    position: 'relative',
                                },
                            }}
                        >
                            {documentRender()}
                        </Box>
                    </Box>
                    <Divider orientation="vertical" flexItem />
                    <Box
                        flex={7}
                        width="100%"
                        minWidth={0}
                        p={5}
                        display='flex'
                        flexDirection='column'
                        gap={3}
                    >
                        {/* VIEW MODE: Show questions list */}
                        {!hwQuestionMode && textbook?.hw_questions && textbook?.hw_questions.length > 0 ? (<>
                            {/* Reorder button - only show if 2+ questions on current page */}
                            {textbook?.hw_questions.filter(q => q.page === selectedPage).length > 1 && (
                                <Box mb={1}>
                                    <ReorderQuestionsModal
                                        questions={textbook.hw_questions.filter(q => q.page === selectedPage)}
                                        setSuccessAlertOpen={setSuccessAlertOpen}
                                        setErrorAlertOpen={setErrorAlertOpen}
                                        setSuccessAlertMessage={setSuccessAlertMessage}
                                        setErrorAlertMessage={setErrorAlertMessage}
                                        setUpdated={setUpdated}
                                    />
                                </Box>
                            )}
                            {textbook?.hw_questions.map((question, index) => {
                                if (question.page !== selectedPage)
                                    return null;

                                return (
                                    <Box
                                        key={index}
                                        sx={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: '20px',
                                            paddingBottom: '20px',
                                            borderBottom: index !== textbook?.hw_questions.length - 1 ? '1px solid lightgrey' : 'none',
                                        }}
                                    >
                                        {/* Question Label */}
                                        <Typography variant="h4" sx={{'fontWeight': 'bold'}}>
                                            {question.question_label} {question.question_type === 'file_upload' ? 'Upload an image file for this question!' : null}
                                        </Typography>

                                        {/* Passage (if exists) */}
                                        {question.passage && (
                                            <Box
                                                sx={{
                                                    padding: '15px',
                                                    borderRadius: '8px',
                                                    backgroundColor: colors.blueAccent[800],
                                                    border: `1px solid ${colors.blueAccent[600]}`,
                                                }}
                                            >
                                                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1, color: colors.blueAccent[300] }}>
                                                    Passage / Context:
                                                </Typography>
                                                <QuestionRender content={question.passage} />
                                            </Box>
                                        )}

                                        {/* Question Content */}
                                        {question.content && (
                                            <Box sx={{ mb: 1 }}>
                                                <QuestionRender content={question.content} />
                                            </Box>
                                        )}

                                        {/* Answer Input Fields */}
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                flexDirection: flexDirectionForAnswer(question.answer_tool, question.question_type),
                                                alignItems: alignItemsForAnswer(question.answer_tool, question.question_type),
                                                gap: '20px',
                                            }}
                                        >
                                            {question.question_type === 'multiple_choice' ? (
                                                <FormControl fullWidth>
                                                    <RadioGroup
                                                        aria-labelledby="demo-controlled-radio-buttons-group"
                                                        name="controlled-radio-buttons-group"
                                                    >
                                                        {(Array.isArray(question.options) ? question.options : question.options?.split('|') || []).map((option, optionIndex) => {
                                                            // Check if this option is the correct answer (0-indexed)
                                                            const isCorrectAnswer = (answer, index) => {
                                                                if (typeof answer === 'number') return answer === index;
                                                                if (typeof answer === 'string') {
                                                                    const num = parseInt(answer, 10);
                                                                    return !isNaN(num) && String(num) === answer.trim() && num === index;
                                                                }
                                                                return false;
                                                            };

                                                            const isCorrect = Array.isArray(question.answer)
                                                                ? question.answer.some(ans => isCorrectAnswer(ans, optionIndex))
                                                                : isCorrectAnswer(question.answer, optionIndex);

                                                            return (
                                                                <FormControlLabel
                                                                    key={optionIndex}
                                                                    value={option}
                                                                    control={
                                                                        <Radio
                                                                            checked={isCorrect}
                                                                            sx={{
                                                                                color: isCorrect ? colors.greenAccent[400] : undefined,
                                                                                '&.Mui-checked': {
                                                                                    color: colors.greenAccent[400],
                                                                                },
                                                                            }}
                                                                        />
                                                                    }
                                                                    label={<QuestionRender content={option} />}
                                                                />
                                                            );
                                                        })}
                                                    </RadioGroup>
                                                </FormControl>
                                            ) : question.question_type === 'short_answer' && question.answer_tool === 'textfield' ? (
                                                <TextField
                                                    variant="outlined"
                                                    type="text"
                                                    label={userRole === 'student' ? 'Your Answer' : 'Student Answer'}
                                                    sx={{ maxWidth: '200px' }}
                                                />
                                            ) : question.question_type === 'short_answer' && question.answer_tool === 'mathlive' ? (
                                                question.answer_config?.allowTextInput === true ? (
                                                    <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                                        <math-field id="mathfield" style={{ width: "200px" }}></math-field>
                                                        <Typography variant="caption" sx={{ mx: 1 }}>or</Typography>
                                                        <TextField
                                                            variant="outlined"
                                                            type="text"
                                                            size="small"
                                                            label="Text answer"
                                                            sx={{ maxWidth: '200px' }}
                                                        />
                                                    </Box>
                                                ) : (
                                                    <>
                                                        <math-field
                                                            id="mathfield"
                                                            style={{ width: "200px" }}
                                                        >

                                                        </math-field>
                                                        <p>{userRole === 'student' ? 'Your Answer' : 'Student Answer'}: </p>
                                                    </>
                                                )
                                            ) : (question.question_type === 'short_answer' || question.question_type === 'long_answer') && question.answer_tool === 'tinymce' ? (
                                                <Box width={'100%'}>
                                                    <EditorTiny 
                                                        value={''} 
                                                        setHWAnswers={null} 
                                                        hw_question_id={question.id}
                                                    />
                                                </Box>
                                            ) : question.question_type === 'long_answer' && question.answer_tool === 'textfield' ? (
                                                <TextField 
                                                    multiline
                                                    variant="outlined"
                                                    type="text"
                                                    label={userRole === 'student' ? 'Your Answer' : 'Student Answer'}
                                                    sx={{ width: '100%' }}
                                                />
                                            ) : question.question_type === 'long_answer' && question.answer_tool === 'mathlive' ? (
                                                <>
                                                    <math-field 
                                                        id="mathfield"
                                                        style={{ width: "100%" }}
                                                    >

                                                    </math-field>
                                                    <p>{userRole === 'student' ? 'Your Answer' : 'Student Answer'}: </p>
                                                </>
                                            ) : question.question_type === 'file_upload' ? (
                                                <Box
                                                    display="flex"
                                                    flexDirection="column"
                                                    sx={{ width: "100%", gap: "10px" }}
                                                >
                                                    <FileUpload
                                                        setHWAnswerFiles={null}
                                                        hwAnswerFiles={null}
                                                        hw_question_id={question.id}
                                                    />
                                                </Box>
                                            ) : question.answer_tool === 'coordinate' ? (
                                                <Box>
                                                    <Typography variant="subtitle2" sx={{ mb: 1, color: colors.grey[400] }}>
                                                        Student Answer Input (Coordinate Grid):
                                                    </Typography>
                                                    <CoordinateGrid
                                                        config={question.answer_config?.grid || defaultCoordinateConfig.grid}
                                                        elements={[]}
                                                        pendingPoints={[]}
                                                        mode="point"
                                                        readOnly={true}
                                                        width={150}
                                                        height={150}
                                                    />
                                                </Box>
                                            ) : question.answer_tool === 'table' ? (
                                                <Box>
                                                    <Typography variant="subtitle2" sx={{ mb: 1, color: colors.grey[400] }}>
                                                        Student Answer Input (Table):
                                                    </Typography>
                                                    <TableReadOnly
                                                        config={question.answer_config || defaultTableConfig}
                                                        studentAnswer={null}
                                                        showCorrect={false}
                                                    />
                                                </Box>
                                            ) : question.answer_tool === 'numberline' ? (
                                                <Box>
                                                    <Typography variant="subtitle2" sx={{ mb: 1, color: colors.grey[400] }}>
                                                        Student Answer Input (Number Line):
                                                    </Typography>
                                                    <NumberLineReadOnly
                                                        config={question.answer_config || defaultNumberLineConfig}
                                                        studentAnswer={null}
                                                        correctAnswer={null}
                                                        showCorrect={false}
                                                    />
                                                </Box>
                                            ) : question.answer_tool === 'mapping' ? (
                                                <Box>
                                                    <Typography variant="subtitle2" sx={{ mb: 1, color: colors.grey[400] }}>
                                                        Student Answer Input (Mapping):
                                                    </Typography>
                                                    <MappingReadOnly
                                                        config={question.answer_config || defaultMappingConfig}
                                                        studentAnswer=""
                                                        correctAnswer=""
                                                        showCorrectAnswer={false}
                                                        showGradeBadge={false}
                                                    />
                                                </Box>
                                            ) : question.answer_tool === 'polynomialGraph' ? (
                                                <Box>
                                                    <GraphStudentPreview
                                                        Component={PolynomialGraphStudent}
                                                        config={question.answer_config}
                                                        defaultConfig={defaultPolynomialGraphConfig}
                                                    />
                                                </Box>
                                            ) : question.answer_tool === 'rationalGraph' ? (
                                                <Box>
                                                    <GraphStudentPreview
                                                        Component={RationalGraphStudent}
                                                        config={question.answer_config}
                                                        defaultConfig={defaultRationalGraphConfig}
                                                    />
                                                </Box>
                                            ) : question.answer_tool === 'trigGraph' ? (
                                                <Box>
                                                    <GraphStudentPreview
                                                        Component={TrigGraphStudent}
                                                        config={question.answer_config}
                                                        defaultConfig={defaultTrigGraphConfig}
                                                    />
                                                </Box>
                                            ) : question.answer_tool === 'multiline' ? (
                                                <Box>
                                                    <Typography variant="subtitle2" sx={{ mb: 1, color: colors.grey[400] }}>
                                                        Student Answer Input (Multiline):
                                                    </Typography>
                                                    <MultilineReadOnly
                                                        config={question.answer_config || defaultMultilineConfig}
                                                        studentAnswer={null}
                                                        showCorrect={false}
                                                    />
                                                </Box>
                                            ) : null}
                                        </Box>
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                gap: '10px',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                            }}
                                        >
                                            {(question.question_type !== 'file_upload' && question.question_type !== 'long_answer' && question.answer_tool !== 'tinymce') && (
                                                <Box
                                                    sx={{
                                                        padding: '10px',
                                                        borderRadius: '5px',
                                                        backgroundColor: colors.primary[750],
                                                        border: '1px solid lightgrey',
                                                    }}
                                                >
                                                    <Typography variant="h5" sx={{'fontWeight': 'bold', mb: 1}}>
                                                        Correct Answer{Array.isArray(question.answer) && question.answer.length > 1 ? 's' : ''}:
                                                    </Typography>
                                                    {question.answer_tool === 'coordinate' ? (
                                                        // Coordinate answer - show visual grid
                                                        (() => {
                                                            const config = question.answer_config?.grid || defaultCoordinateConfig.grid;
                                                            let elements = [];
                                                            try {
                                                                if (question.answer && question.answer[0]) {
                                                                    const parsed = JSON.parse(question.answer[0]);
                                                                    elements = hydrateElements(parsed?.elements || [], config);
                                                                }
                                                            } catch (e) {
                                                                console.error('Error parsing coordinate answer:', e);
                                                            }

                                                            return (
                                                                <>
                                                                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                                                                        <CoordinateGrid
                                                                            config={config}
                                                                            elements={elements}
                                                                            pendingPoints={[]}
                                                                            mode="point"
                                                                            readOnly={true}
                                                                            width={150}
                                                                            height={150}
                                                                        />
                                                                        <Box sx={{ flex: 1, minWidth: 120 }}>
                                                                            <Typography variant="caption" sx={{ color: colors.grey[400], display: 'block', mb: 1 }}>
                                                                                {elements.length} element{elements.length !== 1 ? 's' : ''}:
                                                                            </Typography>
                                                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                                                {elements.length === 0 ? (
                                                                                    <Typography variant="caption" sx={{ color: colors.grey[500], fontStyle: 'italic' }}>
                                                                                        No answer set
                                                                                    </Typography>
                                                                                ) : (
                                                                                    elements.map((el, i) => (
                                                                                        <Chip
                                                                                            key={el.id || i}
                                                                                            label={formatElementSummary(el)}
                                                                                            size="small"
                                                                                            sx={{
                                                                                                backgroundColor: colors.greenAccent[900],
                                                                                                color: colors.grey[200],
                                                                                                fontSize: '0.7rem',
                                                                                            }}
                                                                                        />
                                                                                    ))
                                                                                )}
                                                                            </Box>
                                                                        </Box>
                                                                    </Box>

                                                                    {/* Answer Validation Logic Summary */}
                                                                    <AnswerLogicSummary
                                                                        elements={elements}
                                                                        answerConfig={question.answer_config}
                                                                        compact={true}
                                                                        colors={colors}
                                                                    />
                                                                </>
                                                            );
                                                        })()
                                                    ) : question.answer_tool === 'table' ? (
                                                        // Table answer - show visual table using answer_config
                                                        (() => {
                                                            // answer_config contains the full table with values in each cell
                                                            const config = question.answer_config || defaultTableConfig;

                                                            // Build studentAnswer from config's cell values
                                                            const answerFromConfig = {
                                                                rows: (config.rows || []).map(row => ({
                                                                    id: row.id,
                                                                    cells: Object.fromEntries(
                                                                        Object.entries(row.cells || {}).map(([colId, cell]) => [
                                                                            colId,
                                                                            cell?.value || ''
                                                                        ])
                                                                    )
                                                                }))
                                                            };

                                                            return (
                                                                <TableReadOnly
                                                                    config={config}
                                                                    studentAnswer={answerFromConfig}
                                                                    showCorrect={false}
                                                                />
                                                            );
                                                        })()
                                                    ) : question.answer_tool === 'mapping' ? (
                                                        // Mapping answer - show visual mapping
                                                        (() => {
                                                            const config = question.answer_config || defaultMappingConfig;
                                                            // Get answer string - handle both array and direct string formats
                                                            const answerString = Array.isArray(question.answer)
                                                                ? question.answer[0]
                                                                : (question.answer || '{"connections": []}');

                                                            return (
                                                                <MappingReadOnly
                                                                    config={config}
                                                                    studentAnswer={answerString}
                                                                    correctAnswer={answerString}
                                                                    showCorrectAnswer={false}
                                                                    showGradeBadge={false}
                                                                />
                                                            );
                                                        })()
                                                    ) : question.answer_tool === 'numberline' ? (
                                                        // Numberline answer - show visual number line
                                                        (() => {
                                                            const nlConfig = question.answer_config || defaultNumberLineConfig;
                                                            let elements = [];
                                                            try {
                                                                if (question.answer && question.answer[0]) {
                                                                    const parsed = JSON.parse(question.answer[0]);
                                                                    elements = parsed?.elements || [];
                                                                }
                                                            } catch (e) {
                                                                console.error('Error parsing numberline answer:', e);
                                                            }
                                                            return (
                                                                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                                                                    <NumberLineSVG
                                                                        config={nlConfig}
                                                                        elements={elements}
                                                                        readOnly={true}
                                                                        width={300}
                                                                        height={80}
                                                                    />
                                                                    <Box sx={{ flex: 1, minWidth: 120 }}>
                                                                        <Typography variant="caption" sx={{ color: colors.grey[400], display: 'block', mb: 1 }}>
                                                                            {elements.length} element{elements.length !== 1 ? 's' : ''}:
                                                                        </Typography>
                                                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                                            {elements.length === 0 ? (
                                                                                <Typography variant="caption" sx={{ color: colors.grey[500], fontStyle: 'italic' }}>
                                                                                    No answer set
                                                                                </Typography>
                                                                            ) : (
                                                                                elements.map((el, i) => (
                                                                                    <Chip
                                                                                        key={el.id || i}
                                                                                        label={formatNLElementSummary(el)}
                                                                                        size="small"
                                                                                        sx={{
                                                                                            backgroundColor: colors.greenAccent[900],
                                                                                            color: colors.grey[200],
                                                                                            fontSize: '0.7rem',
                                                                                        }}
                                                                                    />
                                                                                ))
                                                                            )}
                                                                        </Box>
                                                                    </Box>
                                                                </Box>
                                                            );
                                                        })()
                                                    ) : question.answer_tool === 'polynomialGraph' ? (
                                                        // Polynomial graph answer - show visual graph
                                                        (() => {
                                                            let answerData = { correctGraphIndex: 0 };
                                                            try {
                                                                if (question.answer && question.answer[0]) {
                                                                    answerData = JSON.parse(question.answer[0]);
                                                                }
                                                            } catch (e) {
                                                                console.error('Error parsing polynomial graph answer:', e);
                                                            }
                                                            return (
                                                                <PolynomialGraphReadOnly
                                                                    config={question.answer_config || defaultPolynomialGraphConfig}
                                                                    studentAnswer={answerData}
                                                                    correctAnswer={answerData}
                                                                />
                                                            );
                                                        })()
                                                    ) : question.answer_tool === 'rationalGraph' ? (
                                                        // Rational graph answer - show visual graph
                                                        (() => {
                                                            let answerData = { correctGraphIndex: 0 };
                                                            try {
                                                                if (question.answer && question.answer[0]) {
                                                                    answerData = JSON.parse(question.answer[0]);
                                                                }
                                                            } catch (e) {
                                                                console.error('Error parsing rational graph answer:', e);
                                                            }
                                                            return (
                                                                <RationalGraphReadOnly
                                                                    config={question.answer_config || defaultRationalGraphConfig}
                                                                    studentAnswer={answerData}
                                                                    correctAnswer={answerData}
                                                                />
                                                            );
                                                        })()
                                                    ) : question.answer_tool === 'trigGraph' ? (
                                                        // Trig graph answer - show visual graph
                                                        (() => {
                                                            let answerData = { correctGraphIndex: 0 };
                                                            try {
                                                                if (question.answer && question.answer[0]) {
                                                                    answerData = JSON.parse(question.answer[0]);
                                                                }
                                                            } catch (e) {
                                                                console.error('Error parsing trig graph answer:', e);
                                                            }
                                                            return (
                                                                <TrigGraphReadOnly
                                                                    config={question.answer_config || defaultTrigGraphConfig}
                                                                    studentAnswer={answerData}
                                                                    correctAnswer={answerData}
                                                                />
                                                            );
                                                        })()
                                                    ) : question.answer_tool === 'multiline' ? (
                                                        (() => {
                                                            let answerData = { rows: [] };
                                                            try {
                                                                if (question.answer && question.answer[0]) {
                                                                    answerData = JSON.parse(question.answer[0]);
                                                                }
                                                            } catch (e) {
                                                                console.error('Error parsing multiline answer:', e);
                                                            }
                                                            return (
                                                                <MultilineReadOnly
                                                                    config={question.answer_config || defaultMultilineConfig}
                                                                    studentAnswer={answerData}
                                                                    correctAnswer={answerData}
                                                                    showCorrect={false}
                                                                />
                                                            );
                                                        })()
                                                    ) : question.answer_tool === 'mathlive' ? (
                                                        (() => {
                                                            const answers = Array.isArray(question.answer) ? question.answer : [question.answer];
                                                            return answers.map((rawAns, idx) => {
                                                                const parsed = parseMathliveAnswer(rawAns);
                                                                if (parsed?.type === MATHLIVE_ANSWER_TYPES.TEXT) {
                                                                    return (
                                                                        <Box key={idx} sx={{ mt: idx > 0 ? 1 : 0, display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                            <Chip label="Text" size="small" />
                                                                            <Typography variant="body2">{parsed.value}</Typography>
                                                                        </Box>
                                                                    );
                                                                }
                                                                return (
                                                                    <Box key={idx} sx={{ mt: idx > 0 ? 1 : 0 }}>
                                                                        <QuestionRender content={String(parsed?.value ?? rawAns ?? '')} />
                                                                    </Box>
                                                                );
                                                            });
                                                        })()
                                                    ) : (
                                                        // Non-coordinate answers - existing logic
                                                        (() => {
                                                            const options = Array.isArray(question.options) ? question.options : question.options?.split('|') || [];
                                                            const answers = Array.isArray(question.answer) ? question.answer : [question.answer];
                                                            const indexLabels = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H'];

                                                            // Check if answers are index-based (for multiple choice)
                                                            const isIndexBased = question.question_type === 'multiple_choice' &&
                                                                answers.length > 0 &&
                                                                answers.every(a => {
                                                                    const num = typeof a === 'number' ? a : parseInt(a, 10);
                                                                    return !isNaN(num) && num >= 0 && num < options.length;
                                                                });

                                                            return answers.map((ans, idx) => {
                                                                if (isIndexBased) {
                                                                    const ansIndex = typeof ans === 'number' ? ans : parseInt(ans, 10);
                                                                    const label = indexLabels[ansIndex] || ansIndex;
                                                                    return (
                                                                        <Box key={idx} sx={{ mt: idx > 0 ? 1 : 0 }}>
                                                                            <Typography component="span" sx={{ fontWeight: 'bold', mr: 1 }}>
                                                                                [{ansIndex}] {label}:
                                                                            </Typography>
                                                                            <QuestionRender content={options[ansIndex]} />
                                                                        </Box>
                                                                    );
                                                                }
                                                                return (
                                                                    <Box key={idx} sx={{ mt: idx > 0 ? 1 : 0 }}>
                                                                        <QuestionRender content={ans} />
                                                                    </Box>
                                                                );
                                                            });
                                                        })()
                                                    )}
                                                </Box>
                                            )}
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    gap: '10px',
                                                    alignItems: 'center',
                                                    marginLeft: 'auto',
                                                }}
                                            >
                                                <ExerciseEditModal
                                                    question={question}
                                                    numPages={numPages}
                                                    setUpdated={setUpdated}
                                                    setSuccessAlertMessage={setSuccessAlertMessage}
                                                    setSuccessAlertOpen={setSuccessAlertOpen}
                                                    setErrorAlertMessage={setErrorAlertMessage}
                                                    setErrorAlertOpen={setErrorAlertOpen}
                                                />
                                                <Button
                                                    variant="contained"
                                                    color="error"
                                                    onClick={(e) => handleDelete(e, question.id, question.question_label)}
                                                >
                                                    Delete
                                                </Button>
                                            </Box>
                                        </Box>
                                    </Box>
                                );
                            })}
                        </>) : !hwQuestionMode && (
                            <Typography variant="h5" sx={{'fontWeight': 'bold'}}>
                                No questions found in this page
                            </Typography>
                        )}

                        {/* ADD MODE: Show appropriate form/tool */}
                        {hwQuestionMode && !showExtractionMonitor && !showQuestionsReview && addSubMode === "manual" && (
                            <HwQuestionForm
                                textbook={textbook}
                                page={selectedPage}
                                numPages={numPages}
                                textbooks={null}
                                showTextbookSelector={false}
                                showPageSelector={false}
                                onSubmit={handleManualQuestionSubmit}
                                onCancel={null}
                                compact={true}
                                showPDF={false}
                            />
                        )}

                        {hwQuestionMode && !showExtractionMonitor && !showQuestionsReview && addSubMode === "crop" && (
                            <HwQuestionCropTool
                                textbook={textbook}
                                page={selectedPage}
                                numPages={numPages}
                                showTextbookSelector={false}
                                showPageSelector={true}
                                textbooks={null}
                                pdfUrl={textbook?.file_url}
                                onExtract={(jobId) => {
                                    setExtractionJobId(jobId);
                                    setShowExtractionMonitor(true);
                                }}
                                onError={handleExtractionError}
                                compact={true}
                                externalPdfContainerRef={pdfContainerRef}
                                useExternalPdf={true}
                            />
                        )}

                        {hwQuestionMode && !showExtractionMonitor && !showQuestionsReview && addSubMode === "page_range" && (
                            <HwQuestionPageRangeSelector
                                textbook={textbook}
                                numPages={numPages}
                                showTextbookSelector={false}
                                textbooks={null}
                                pdfUrl={textbook?.file_url}
                                onExtract={(jobId) => {
                                    setExtractionJobId(jobId);
                                    setShowExtractionMonitor(true);
                                }}
                                onError={handleExtractionError}
                                compact={true}
                            />
                        )}

                        {/* EXTRACTION MONITOR */}
                        {showExtractionMonitor && (
                            <Box>
                                <Typography variant="h5" fontWeight="bold" mb={3}>
                                    Extracting Questions...
                                </Typography>
                                <ExtractionJobMonitor
                                    jobId={extractionJobId}
                                    onComplete={(questions) => {
                                        setExtractedQuestions(questions);
                                        setShowExtractionMonitor(false);
                                        setShowQuestionsReview(true);
                                        setSuccessAlertMessage(`Successfully extracted ${questions.length} questions!`);
                                        setSuccessAlertOpen(true);
                                    }}
                                    onError={(message) => {
                                        setShowExtractionMonitor(false);
                                        handleExtractionError(message);
                                    }}
                                />
                            </Box>
                        )}

                        {/* QUESTIONS REVIEW */}
                        {showQuestionsReview && (
                            <Box>
                                <Typography variant="h5" fontWeight="bold" mb={3}>
                                    Review Extracted Questions
                                </Typography>
                                <ExtractedQuestionsReview
                                    questions={extractedQuestions}
                                    textbooks={[textbook]}
                                    extractionJobId={extractionJobId}
                                    onSave={() => {
                                        setShowQuestionsReview(false);
                                        setExtractedQuestions([]);
                                        setExtractionJobId(null);
                                        setSuccessAlertMessage("All questions saved successfully!");
                                        setSuccessAlertOpen(true);
                                        setUpdated(true);
                                        setHwQuestionMode(false);
                                    }}
                                    onCancel={() => {
                                        setShowQuestionsReview(false);
                                        setExtractedQuestions([]);
                                        setExtractionJobId(null);
                                    }}
                                    onError={handleExtractionError}
                                    onSaveDraft={() => {
                                        setSuccessAlertMessage("Extraction job saved successfully!");
                                        setSuccessAlertOpen(true);
                                    }}
                                    onPageClick={(page) => setSelectedPage(page)}
                                />
                            </Box>
                        )}
                    </Box>
                </Box>
            </>}
            <Dialog
                open={showRecentJobsModal}
                onClose={() => setShowRecentJobsModal(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>
                    Recent Extraction Jobs
                    <IconButton onClick={() => setShowRecentJobsModal(false)} sx={{ position: 'absolute', right: 8, top: 8 }}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    <RecentExtractionJobs
                        textbookId={textbookId}
                        textbooks={[textbook]}
                        onViewResults={(questions, jobId, firstPage) => {
                            setShowRecentJobsModal(false);
                            setExtractedQuestions(questions);
                            setExtractionJobId(jobId);
                            setShowQuestionsReview(true);
                            setHwQuestionMode(true);
                            if (firstPage) setSelectedPage(firstPage);
                        }}
                        onError={handleExtractionError}
                    />
                </DialogContent>
            </Dialog>
            <Alert
                successAlertOpen={successAlertOpen}
                setSuccessAlertOpen={setSuccessAlertOpen}
                errorAlertOpen={errorAlertOpen}
                setErrorAlertOpen={setErrorAlertOpen}
                successMsg={successAlertMessage}
                errorMsg={errorAlertMessage}
                vertical="bottom"
                horizontal="left"
            />
            <ConfirmDialog ref={confirmRef} />
        </Box>
    )
}

export default TextbookExercises